import React, { useState, useEffect } from 'react';
import { loadGuardiaQuestions, saveFlashcardProgress, getFlashcardProgress, getCorrectAnswerText } from '../utils/studyDataService';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';

const Flashcards = () => {
  const { user, profileData, profileLoading } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [selectedMateria, setSelectedMateria] = useState('ALL');
  const [materias, setMaterias] = useState([]);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cardProgress, setCardProgress] = useState({});
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Cargar preguntas y progreso guardado. Se espera a que el perfil resuelva
  // para no cargar el banco SEMAR antes de conocer la institución del usuario.
  useEffect(() => {
    if (profileLoading || !user) return undefined;
    let active = true;

    async function init() {
      setLoading(true);
      try {
        const resultado = await loadGuardiaQuestions(profileData, user.uid);
        if (!active) return;
        const data = resultado.questions || [];
        setQuestions(data);

        const uniqueMaterias = Array.from(new Set(data.map(q => q.materia || 'General'))).filter(Boolean);
        setMaterias(uniqueMaterias);
        setSelectedMateria('ALL');

        const prog = getFlashcardProgress();
        setCardProgress(prog);

        setFilteredQuestions(data);
      } catch (e) {
        console.error('Error cargando flashcards:', e);
        if (!active) return;
        setQuestions([]);
        setFilteredQuestions([]);
        setMaterias([]);
        setSelectedMateria('ALL');
      } finally {
        if (active) setLoading(false);
      }
    }
    init();

    return () => {
      active = false;
    };
  }, [
    user?.uid,
    profileLoading,
    profileData?.institucion,
    profileData?.grado,
    profileData?.jerarquia,
    profileData?.hierarchy,
    profileData?.cuerpoServicio,
    profileData?.cuerpo,
    profileData?.corps,
    profileData?.especialidad,
    profileData?.specialty,
  ]);

  // Filtrar por materia seleccionada
  useEffect(() => {
    let list = [...questions];
    if (selectedMateria !== 'ALL') {
      list = list.filter(q => (q.materia || 'General') === selectedMateria);
    }
    setFilteredQuestions(list);
    setCurrentIndex(0);
    setIsFlipped(false);
  }, [selectedMateria, questions]);

  const currentCard = filteredQuestions[currentIndex] || null;

  // Manejar evaluación con repetición espaciada
  const handleRating = (level) => {
    if (!currentCard) return;
    
    // Guardar en localStorage
    saveFlashcardProgress(currentCard.id, level);
    
    // Actualizar estado local
    setCardProgress(prev => ({
      ...prev,
      [currentCard.id]: { level, timestamp: Date.now() }
    }));

    // Voltear de vuelta y pasar a la siguiente tarjeta
    setIsFlipped(false);
    setTimeout(() => {
      if (currentIndex < filteredQuestions.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        // Al llegar al final, reiniciar al inicio
        setCurrentIndex(0);
      }
    }, 200);
  };

  // Reproducir voz
  const speakCurrentCard = () => {
    if (!currentCard || !('speechSynthesis' in window)) return;

    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const textToSpeak = isFlipped
      ? `Respuesta correcta: ${getCorrectAnswerText(currentCard)}. Fundamento: ${currentCard.explicacion || ''}`
      : `Pregunta: ${currentCard.pregunta}`;

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = 'es-MX';
    utterance.rate = 1.0;

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  // Barajar tarjetas aleatoriamente
  const shuffleCards = () => {
    const shuffled = [...filteredQuestions].sort(() => Math.random() - 0.5);
    setFilteredQuestions(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  // Conteo de tarjetas
  const totalCount = filteredQuestions.length;
  const easyCount = filteredQuestions.filter(q => cardProgress[q.id]?.level === 'EASY').length;
  const hardCount = filteredQuestions.filter(q => cardProgress[q.id]?.level === 'HARD' || cardProgress[q.id]?.level === 'MEDIUM').length;

  if (loading) return <LoadingSpinner message="Cargando Tarjetas de Memoria (Flashcards)..." />;

  return (
    <div style={{
      padding: '1.25rem',
      color: 'var(--text-primary)',
      height: 'calc(100vh - 60px)',
      boxSizing: 'border-box',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem'
    }}>
      {/* Encabezado Principal */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        borderBottom: '1px solid rgba(197, 160, 89, 0.2)',
        paddingBottom: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h1 style={{
            color: '#c5a059',
            fontFamily: "'Orbitron', sans-serif",
            fontSize: '1.25rem',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            📇 Tarjetas de Memoria (Flashcards)
          </h1>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', padding: '4px 10px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid #10b981' }}>
              🟢 Dominadas: {easyCount}
            </span>
            <span style={{ fontSize: '0.75rem', padding: '4px 10px', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid #ef4444' }}>
              🔴 Repasar: {hardCount}
            </span>
          </div>
        </div>

        <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(181, 200, 216, 0.8)' }}>
          Repetición Espaciada: Evalúa tu retención con los 3 niveles para enfocar tu estudio en tus áreas de oportunidad.
        </p>
      </div>

      {/* Controles de Filtro y Acción */}
      <div style={{
        display: 'flex',
        justify: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        background: 'rgba(10, 26, 42, 0.8)',
        padding: '12px 16px',
        borderRadius: '10px',
        border: '1px solid rgba(197, 160, 89, 0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '240px' }}>
          <label style={{ fontSize: '0.75rem', color: '#c5a059', fontFamily: "'Orbitron', sans-serif", textTransform: 'uppercase' }}>
            Materia:
          </label>
          <select
            value={selectedMateria}
            onChange={(e) => setSelectedMateria(e.target.value)}
            style={{
              flex: 1,
              background: '#071526',
              color: '#fff',
              border: '1px solid rgba(197, 160, 89, 0.4)',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '0.8rem',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="ALL">📚 Todas las Materias ({questions.length})</option>
            {materias.map((mat, idx) => (
              <option key={idx} value={mat}>{mat}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={shuffleCards}
            style={{
              background: 'rgba(197, 160, 89, 0.12)',
              border: '1px solid #c5a059',
              borderRadius: '6px',
              color: '#c5a059',
              fontSize: '0.75rem',
              padding: '8px 14px',
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🔀 Mezclar
          </button>

          <button
            onClick={speakCurrentCard}
            style={{
              background: isSpeaking ? 'rgba(239, 68, 68, 0.2)' : 'rgba(96, 165, 250, 0.15)',
              border: `1px solid ${isSpeaking ? '#ef4444' : '#60a5fa'}`,
              borderRadius: '6px',
              color: isSpeaking ? '#f87171' : '#60a5fa',
              fontSize: '0.75rem',
              padding: '8px 14px',
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🔊 {isSpeaking ? 'Detener Voz' : 'Escuchar'}
          </button>
        </div>
      </div>

      {/* Contenedor de la Tarjeta 3D */}
      {currentCard ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', alignItems: 'center', flex: 1, minHeight: 0, justifyContent: 'center', overflowY: 'auto', width: '100%' }}>
          {/* Tarjeta Flip */}
  <div
  role="button"
  tabIndex={0}
  onClick={() => setIsFlipped(!isFlipped)}
  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsFlipped((v) => !v); } }}
            style={{
              width: '100%',
              maxWidth: '480px',
              minHeight: '320px',
              perspective: '1000px',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <div style={{
              width: '100%',
              height: '100%',
              minHeight: '320px',
              position: 'relative',
              transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              background: 'linear-gradient(145deg, #0d2035 0%, #071526 100%)',
              border: '2px solid #c5a059',
              borderRadius: '16px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.7), 0 0 20px rgba(197,160,89,0.15)',
              display: 'flex',
              flexDirection: 'column',
              padding: '24px'
            }}>
              {/* LADO ANVERSO (PREGUNTA) */}
              <div style={{
                position: isFlipped ? 'absolute' : 'relative',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                visibility: isFlipped ? 'hidden' : 'visible'
              }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{
                      fontSize: '0.65rem',
                      fontFamily: "'Orbitron', sans-serif",
                      letterSpacing: '1px',
                      color: '#c5a059',
                      background: 'rgba(197, 160, 89, 0.15)',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      textTransform: 'uppercase'
                    }}>
                      {currentCard.materia || 'Legislación'}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'rgba(181, 200, 216, 0.6)' }}>
                      Tarjeta {currentIndex + 1} / {totalCount}
                    </span>
                  </div>

                  <h3 style={{
                    color: '#ffffff',
                    fontSize: '1.1rem',
                    lineHeight: '1.6',
                    fontWeight: '600',
                    marginTop: '10px'
                  }}>
                    {currentCard.pregunta}
                  </h3>
                </div>

                <div style={{
                  marginTop: '20px',
                  textAlign: 'center',
                  padding: '12px',
                  background: 'rgba(197, 160, 89, 0.08)',
                  borderRadius: '8px',
                  border: '1px dashed rgba(197, 160, 89, 0.3)',
                  color: '#c5a059',
                  fontSize: '0.8rem',
                  fontFamily: "'Orbitron', sans-serif"
                }}>
                  🔄 Haz clic o toca para VER LA RESPUESTA
                </div>
              </div>

              {/* LADO REVERSO (RESPUESTA Y FUNDAMENTO) */}
              <div style={{
                position: isFlipped ? 'relative' : 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                visibility: isFlipped ? 'visible' : 'hidden'
              }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{
                      fontSize: '0.65rem',
                      fontFamily: "'Orbitron', sans-serif",
                      letterSpacing: '1px',
                      color: '#34d399',
                      background: 'rgba(16, 185, 129, 0.15)',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      textTransform: 'uppercase'
                    }}>
                      ✓ RESPUESTA CORRECTA
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'rgba(181, 200, 216, 0.6)' }}>
                      Tarjeta {currentIndex + 1} / {totalCount}
                    </span>
                  </div>

                  <div style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    backgroundImage: 'linear-gradient(to right, rgba(76,175,80,0.14), transparent 45%)',
                    padding: '14px',
                    borderRadius: '6px',
                    marginBottom: '14px'
                  }}>
                    <p style={{ margin: 0, color: '#34d399', fontSize: '1rem', fontWeight: 'bold', lineHeight: '1.5' }}>
                      {getCorrectAnswerText(currentCard)}
                    </p>
                  </div>

                  {currentCard.explicacion && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.04)',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '0.82rem',
                      color: 'rgba(181, 200, 216, 0.9)',
                      lineHeight: '1.5'
                    }}>
                      <strong style={{ color: '#c5a059', display: 'block', marginBottom: '4px' }}>
                        ⚖️ Fundamento / Explicación:
                      </strong>
                      {currentCard.explicacion}
                    </div>
                  )}
                </div>

                <div style={{
                  marginTop: '16px',
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  color: 'rgba(197, 160, 89, 0.8)',
                  fontFamily: "'Orbitron', sans-serif"
                }}>
                  Califica tu nivel de retención abajo ⬇️
                </div>
              </div>
            </div>
          </div>

          {/* Botones de Calificación de Repetición Espaciada */}
          {isFlipped ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '10px',
              width: '50%'
            }}>
              <button
                onClick={() => handleRating('HARD')}
                style={{
                  background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
                  border: '1px solid #ef4444',
                  borderRadius: '10px',
                  color: '#fff',
                  padding: '14px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>🔴</span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: '0.75rem', fontWeight: 'bold' }}>Repasar</span>
                <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>No la recordaba</span>
              </button>

              <button
                onClick={() => handleRating('MEDIUM')}
                style={{
                  background: 'linear-gradient(135deg, #78350f 0%, #92400e 100%)',
                  border: '1px solid #f59e0b',
                  borderRadius: '10px',
                  color: '#fff',
                  padding: '14px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>🟡</span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: '0.75rem', fontWeight: 'bold' }}>Duda</span>
                <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>Tuve dudas</span>
              </button>

              <button
                onClick={() => handleRating('EASY')}
                style={{
                  background: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)',
                  border: '1px solid #10b981',
                  borderRadius: '10px',
                  color: '#fff',
                  padding: '14px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>🟢</span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: '0.75rem', fontWeight: 'bold' }}>Fácil</span>
                <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>Dominado</span>
              </button>
            </div>
          ) : (
            /* Navegación Anterior/Siguiente */
            <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'space-between' }}>
              <button
                onClick={() => {
                  setIsFlipped(false);
                  setCurrentIndex(prev => (prev > 0 ? prev - 1 : filteredQuestions.length - 1));
                }}
                style={{
                  flex: 1,
                  background: 'rgba(10, 26, 42, 0.8)',
                  border: '1px solid rgba(197, 160, 89, 0.4)',
                  borderRadius: '8px',
                  color: '#c5a059',
                  padding: '12px',
                  fontSize: '0.8rem',
                  fontFamily: "'Orbitron', sans-serif",
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ⬅️ Anterior
              </button>

              <button
                onClick={() => setIsFlipped(true)}
                style={{
                  flex: 1.5,
                  background: 'linear-gradient(135deg, #c5a059 0%, #a88440 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#071526',
                  padding: '12px',
                  fontSize: '0.8rem',
                  fontFamily: "'Orbitron', sans-serif",
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(197, 160, 89, 0.3)'
                }}
              >
                👀 Ver Respuesta
              </button>

              <button
                onClick={() => {
                  setIsFlipped(false);
                  setCurrentIndex(prev => (prev < filteredQuestions.length - 1 ? prev + 1 : 0));
                }}
                style={{
                  flex: 1,
                  background: 'rgba(10, 26, 42, 0.8)',
                  border: '1px solid rgba(197, 160, 89, 0.4)',
                  borderRadius: '8px',
                  color: '#c5a059',
                  padding: '12px',
                  fontSize: '0.8rem',
                  fontFamily: "'Orbitron', sans-serif",
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Siguiente ➡️
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: 'rgba(10, 26, 42, 0.8)',
          borderRadius: '12px',
          border: '1px solid rgba(197, 160, 89, 0.3)'
        }}>
          <h3>No hay tarjetas en esta categoría</h3>
          <p style={{ color: 'rgba(181, 200, 216, 0.7)' }}>Selecciona otra materia o restablece el filtro.</p>
        </div>
      )}
    </div>
  );
};

export default Flashcards;
